import { yupResolver } from "@hookform/resolvers/yup";
import { isAxiosError } from "axios";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import { Card, CardContent } from "@krea/shared/design-system/Card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@krea/shared/design-system/Form";
import { Input } from "@krea/shared/design-system/inputs/Input";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import type { ILenderUser } from "../types";

const profileSchema = yup.object().shape({
  firstName: yup.string(),
  lastName: yup.string(),
  email: yup.string().email("Invalid email"),
  mobilePhoneNumber: yup.string(),
});

type ProfileSectionFormValues = yup.InferType<typeof profileSchema>;

interface IProfileSectionProps {
  user: ILenderUser;
  onUpdateUser: (data: ProfileSectionFormValues) => Promise<void>;
  onClose?: () => void;
}

export const ProfileSection = ({
  user,
  onUpdateUser,
  onClose,
}: IProfileSectionProps) => {
  const form = useForm<ProfileSectionFormValues>({
    resolver: yupResolver(profileSchema),
    mode: "onTouched",
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email ?? "",
      mobilePhoneNumber: user.mobilePhoneNumber ?? "",
    },
  });

  const { isDirty, isValid, isSubmitting } = form.formState;

  useEffect(() => {
    const initialValues: ProfileSectionFormValues = {
      firstName: user.firstName,
      lastName: user.lastName,
    };

    if (user.email) {
      initialValues.email = user.email;
    }

    if (user.mobilePhoneNumber) {
      initialValues.mobilePhoneNumber = user.mobilePhoneNumber;
    }
    form.reset(initialValues);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = useCallback(() => {
    form.handleSubmit(async (data) => {
      try {
        await onUpdateUser(data);
        toast.success(
          <span>
            User profile updated successfully: <br />
            Name: {data.firstName} {data.lastName} <br />
            {data.email && (
              <>
                Email: {data.email} <br />
              </>
            )}
            {data.mobilePhoneNumber && (
              <>
                Mobile Phone Number: {data.mobilePhoneNumber} <br />
              </>
            )}
          </span>,
        );
      } catch (e) {
        const apiError = isAxiosError<{
          errors?: Array<{
            message?: string;
            code?: string;
            field?: string | null;
          }>;
        }>(e)
          ? e.response?.data?.errors?.[0]
          : null;

        // Backend can return duplicate email with `field: null`; map to email for inline feedback.
        if (apiError?.code === "1005") {
          form.setError("email", {
            type: "server",
            message: apiError.message ?? "Email already exists",
          });
        }

        toast.error(
          apiError?.message ??
            (e instanceof Error ? e.message : "Failed to update user profile"),
        );
      }
    })();
  }, [form, onUpdateUser]);

  return (
    <Card>
      <Form {...form}>
        <CardContent className="space-y-6">
          <div>
            <h1 className="m-0 text-xl font-bold">Profile Information</h1>
          </div>

          <Separator />

          <div>
            <label htmlFor="ssn" className="mb-2 text-sm font-medium">
              SSN
            </label>

            <div className="flex-1">
              <Input value={user.ssn} readOnly disabled />
            </div>

            <p className="mt-2 text-xs text-black-100">
              If you need to change the ssn, contact our customer support team
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field, formState: { isSubmitting } }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="John"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field, formState: { isSubmitting } }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Doe"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field, formState: { isSubmitting } }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="john.doe@example.com"
                    {...field}
                    disabled={isSubmitting}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="mobilePhoneNumber"
            render={({ field, formState: { isSubmitting } }) => (
              <FormItem>
                <FormLabel>Mobile Phone Number</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Mobile phone number"
                    {...field}
                    disabled={isSubmitting}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />
          <Separator />
          <div className="flex justify-end">
            {!!onClose && !isDirty ? (
              <Button variant="secondary" size="lg" onClick={onClose}>
                Close
              </Button>
            ) : (
              <Button
                type="button"
                size="lg"
                onClick={onSubmit}
                disabled={isSubmitting || !isValid || !isDirty}
              >
                Save Configuration
              </Button>
            )}
          </div>
        </CardContent>
      </Form>
    </Card>
  );
};
